/**
 * UI adapter: bridges React effects to EvaluateEnhancedObjectivesUseCase (UC-23)
 * Enable via architectureFeatures.useEnhancedObjectivesUseCase
 */
import { useCallback } from 'react';
import { evaluateEnhancedObjectivesUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import type { EnhancedObjective } from '../../../../types/enhancedObjectives';
import type {
  EvaluateEnhancedObjectivesInput,
  EvaluateEnhancedObjectivesOutput,
} from '../../../application/use-cases/EvaluateEnhancedObjectivesUseCase';

/** Register enhanced objectives through clean architecture use case (UC-23) */
export function registerEnhancedObjectives(objectives: EnhancedObjective[]): void {
  if (!architectureFeatures.useEnhancedObjectivesUseCase) {
    throw new Error('useEnhancedObjectivesUseCase feature flag is disabled');
  }

  evaluateEnhancedObjectivesUseCase.registerObjectives(objectives);
}

/** Run enhanced objectives evaluation through clean architecture use case (UC-23) */
export function executeEvaluateEnhancedObjectives(
  input: EvaluateEnhancedObjectivesInput,
): EvaluateEnhancedObjectivesOutput {
  if (!architectureFeatures.useEnhancedObjectivesUseCase) {
    throw new Error('useEnhancedObjectivesUseCase feature flag is disabled');
  }

  return evaluateEnhancedObjectivesUseCase.execute(input);
}

/**
 * Hook helper for useEnhancedObjectivesIntegration.
 * Returns stable callbacks that run UC-23 when feature flag is on.
 */
export function useEnhancedObjectivesEvaluationRunner() {
  const runRegisterObjectives = useCallback((objectives: EnhancedObjective[]): void | null => {
    if (!architectureFeatures.useEnhancedObjectivesUseCase) {
      return null;
    }
    registerEnhancedObjectives(objectives);
  }, []);

  const runEvaluateEnhancedObjectives = useCallback(
    (input: EvaluateEnhancedObjectivesInput): EvaluateEnhancedObjectivesOutput | null => {
      if (!architectureFeatures.useEnhancedObjectivesUseCase) {
        return null;
      }
      return executeEvaluateEnhancedObjectives(input);
    },
    [],
  );

  return { runRegisterObjectives, runEvaluateEnhancedObjectives };
}